import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatDateTick, formatPct } from "../lib/format";

export interface ChartSeries {
  name: string;
  color: string;
  points: { t: string; r: number }[];
}

/** Latest return of a series (its last point); -Infinity if it has no points. */
export function lastReturn(s: ChartSeries): number {
  return s.points.length ? s.points[s.points.length - 1].r : -Infinity;
}

/** Cumulative-return line chart. One line per series (1 = a manager, N = the race). */
export function ReturnChart({
  series,
  height = 240,
  xAxis = false,
}: {
  series: ChartSeries[];
  height?: number | string;
  xAxis?: boolean;
}) {
  const totalPoints = series.reduce((n, s) => n + s.points.length, 0);
  if (totalPoints < 2) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border border-dashed border-edge text-sm text-faint"
        style={{ height }}
      >
        Not enough history yet — fills in as the season runs.
      </div>
    );
  }

  // Merge series onto a shared time axis (cron samples all managers together).
  const times = [...new Set(series.flatMap((s) => s.points.map((p) => p.t)))].sort();
  const data = times.map((t) => {
    const row: Record<string, number | string> = { t };
    for (const s of series) {
      const pt = s.points.find((p) => p.t === t);
      if (pt) row[s.name] = pt.r;
    }
    return row;
  });

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#1a2230" vertical={false} />
          {xAxis ? (
            <XAxis
              dataKey="t"
              tickFormatter={formatDateTick}
              tick={{ fill: "#8b98a9", fontSize: 11 }}
              stroke="#243042"
              minTickGap={60}
              tickMargin={8}
              interval="preserveStartEnd"
            />
          ) : (
            <XAxis dataKey="t" hide />
          )}
          <YAxis
            tickFormatter={(v) => formatPct(v, 0)}
            width={48}
            tick={{ fill: "#8b98a9", fontSize: 11 }}
            stroke="#243042"
          />
          <ReferenceLine y={0} stroke="#3a4757" strokeDasharray="3 3" />
          <Tooltip
            contentStyle={{
              background: "#0e141d",
              border: "1px solid #243042",
              borderRadius: 10,
              fontSize: 12,
            }}
            labelStyle={{ display: "none" }}
            formatter={(v: number, name: string) => [formatPct(v), name]}
          />
          {series.map((s) => (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
